import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faInfoCircle,
  faShip,
  faBuilding,
  faFileInvoice,
  faIdCard,
  faMapMarkerAlt,
  faTruck,
  faCalendarAlt,
  faDownload,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faHourglassHalf,
  faClipboardCheck,
  faHistory,
  faChartBar,
} from "@fortawesome/free-solid-svg-icons";

const DetailedReferencePanel = ({
  item,
  show,
  onClose,
  formatDate,
  getStatusColorClass,
  getTransportIcon,
  calculateEtaCompliance,
  calculateExitTime,
  calculatePaymentTime,
  isInValidationProcess,
  isOutOfTimeValidation,
}) => {
  if (!item) return null;

  // Calculate KPIs
  const etaCompliance = calculateEtaCompliance(item);
  const exitTime = calculateExitTime(item);
  const paymentTime = calculatePaymentTime(item);
  const inValidationProcess = isInValidationProcess(item);
  const outOfTimeValidation = isOutOfTimeValidation(item);

  return (
    <div
      className="fixed right-0 top-0 bottom-0 z-40 shadow-xl border-l border-gray-200 max-h-full flex flex-col bg-white transition-transform duration-300 ease-in-out"
      style={{
        width: "36rem",
        transform: show ? "translateX(0)" : "translateX(100%)",
      }}
    >
      <div className="p-4 bg-gradient-to-r from-[#003366] to-blue-700 text-white flex justify-between items-center">
        <h3 className="text-lg font-medium flex items-center">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
          Reference Details
        </h3>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="space-y-6">
          {/* KPI Status Indicators */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 grid grid-cols-3 gap-4">
            {/* ETA Compliance Indicator */}
            <div className="flex items-start">
              <div
                className={`rounded-full p-2 ${
                  etaCompliance.compliant
                    ? "bg-green-100 text-green-600"
                    : etaCompliance.daysDifference === null
                    ? "bg-gray-100 text-gray-600"
                    : "bg-red-100 text-red-600"
                }`}
              >
                <FontAwesomeIcon
                  icon={
                    etaCompliance.daysDifference === null
                      ? faInfoCircle
                      : etaCompliance.compliant
                      ? faCheckCircle
                      : faTimesCircle
                  }
                />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-500">
                  ETA Compliance
                </p>
                <p className="text-sm font-semibold">
                  {etaCompliance.daysDifference === null
                    ? "No Data"
                    : etaCompliance.compliant
                    ? `Compliant (${etaCompliance.daysDifference} days)`
                    : `Non-compliant (${etaCompliance.daysDifference} days)`}
                </p>
              </div>
            </div>

            {/* Validation Process Indicator */}
            <div className="flex items-start">
              <div
                className={`rounded-full p-2 ${
                  outOfTimeValidation
                    ? "bg-red-100 text-red-600"
                    : inValidationProcess
                    ? "bg-yellow-100 text-yellow-600"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                <FontAwesomeIcon
                  icon={
                    outOfTimeValidation
                      ? faExclamationTriangle
                      : inValidationProcess
                      ? faHourglassHalf
                      : faInfoCircle
                  }
                />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-500">
                  Validation Process
                </p>
                <p className="text-sm font-semibold">
                  {outOfTimeValidation
                    ? "Out of Time"
                    : inValidationProcess
                    ? "In Process"
                    : "Not in validation"}
                </p>
              </div>
            </div>

            {/* Current Status Indicator */}
            <div className="flex items-start">
              <div className={`rounded-full p-2 bg-blue-100 text-blue-600`}>
                <FontAwesomeIcon icon={faClipboardCheck} />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-500">
                  Current Status
                </p>
                <p className="text-sm">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColorClass(
                      item.header.trafficStatus
                    )}`}
                  >
                    {item.header.trafficStatus || "N/A"}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Reference Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="mr-2 text-blue-600"
              />
              Reference Information
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Reference:</span>
                <p className="font-medium">{item.header.reference}</p>
              </div>
              <div>
                <span className="text-gray-500">Operation Type:</span>
                <p className="font-medium">
                  {item.header.operation_type === "1"
                    ? "Import"
                    : item.header.operation_type === "2"
                    ? "Export"
                    : item.header.operation_type || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Trailer Number:</span>
                <p className="font-medium">
                  {item.header.trailer_number || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Purchase Order:</span>
                <p className="font-medium">
                  {item.header.purchase_order || "N/A"}
                </p>
              </div>
              {item.header.bill_of_lading_data &&
                item.header.bill_of_lading_data.length > 0 && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Bill of Lading:</span>
                    <p className="font-medium">
                      {item.header.bill_of_lading_data[0].bill_of_lading ||
                        "N/A"}
                    </p>
                  </div>
                )}
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
              <FontAwesomeIcon
                icon={faBuilding}
                className="mr-2 text-blue-600"
              />
              Customer Information
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Customer Name:</span>
                <p className="font-medium">
                  {item.header.customer_name || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Customer ID:</span>
                <p className="font-medium">
                  {item.header.customer_id || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Customer RFC:</span>
                <p className="font-medium">
                  {item.header.customer_rfc || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Division:</span>
                <p className="font-medium">
                  {item.header.customer_division || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Pedimento Information */}
          {item.header.pedimento && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
                <FontAwesomeIcon
                  icon={faFileInvoice}
                  className="mr-2 text-blue-600"
                />
                Pedimento Information
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Pedimento Number:</span>
                  <p className="font-medium">
                    {item.header.pedimento.pedimento_number || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Entry Point:</span>
                  <p className="font-medium flex items-center">
                    <FontAwesomeIcon
                      icon={
                        getTransportIcon(
                          item.header.pedimento.pedimento_entry_point
                        ).icon
                      }
                      className={`${
                        getTransportIcon(
                          item.header.pedimento.pedimento_entry_point
                        ).color
                      } mr-1`}
                    />
                    {item.header.pedimento.pedimento_entry_point || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">License:</span>
                  <p className="font-medium">
                    {item.header.pedimento.pedimento_license || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Consolidation:</span>
                  <p className="font-medium">
                    {item.header.pedimento.pedimento_consolidation || "N/A"}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Remesa:</span>
                  <p className="font-medium">
                    {item.header.pedimento.pedimento_remesa || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Transport Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
              <FontAwesomeIcon icon={faTruck} className="mr-2 text-blue-600" />
              Transport Information
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Total Packages:</span>
                <p className="font-medium">
                  {item.header.total_packages || "N/A"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Weight:</span>
                <p className="font-medium">
                  {item.header.weight ? `${item.header.weight} kg` : "N/A"}
                </p>
              </div>
              {item.header.detail?.traffic_data && (
                <>
                  <div>
                    <span className="text-gray-500">Transport Code:</span>
                    <p className="font-medium">
                      {item.header.detail.traffic_data.transport_code || "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Outbound Transport:</span>
                    <p className="font-medium">
                      {item.header.detail.traffic_data
                        .transport_code_outbound || "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Carrier:</span>
                    <p className="font-medium">
                      {item.header.detail.traffic_data.us_carrier || "N/A"}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Mexican Carrier:</span>
                    <p className="font-medium">
                      {item.header.detail.traffic_data.mexican_carrier || "N/A"}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Key Dates Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="mr-2 text-blue-600"
              />
              Key Dates
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {/* eBooking Dates */}
              {item.header.ebookingDates && (
                <>
                  <div>
                    <span className="text-gray-500">Alta Date:</span>
                    <p className="font-medium">
                      {formatDate(item.header.ebookingDates.fechaAlta)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">ETA Date:</span>
                    <p className="font-medium">
                      {formatDate(item.header.ebookingDates.fechaETA)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Revalidacion Date:</span>
                    <p className="font-medium">
                      {formatDate(item.header.ebookingDates.fechaRevalidacion)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Piso Date:</span>
                    <p className="font-medium">
                      {formatDate(item.header.ebookingDates.fechaPiso)}
                    </p>
                  </div>
                </>
              )}

              {/* Traffic Dates */}
              {item.header.detail?.dates_data && (
                <>
                  <div>
                    <span className="text-gray-500">Init Date:</span>
                    <p className="font-medium">
                      {formatDate(item.header.detail.dates_data.date_init)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Inbound Date:</span>
                    <p className="font-medium">
                      {formatDate(item.header.detail.dates_data.date_inbound)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Document Date:</span>
                    <p className="font-medium">
                      {formatDate(item.header.detail.dates_data.date_doda)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Pedimento Paid:</span>
                    <p className="font-medium">
                      {formatDate(
                        item.header.detail.dates_data.date_pedimento_paid
                      )}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Crossing Date:</span>
                    <p className="font-medium">
                      {formatDate(item.header.detail.dates_data.date_crossing)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">End Date:</span>
                    <p className="font-medium">
                      {formatDate(item.header.detail.dates_data.date_end)}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* KPI Analysis */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
              <FontAwesomeIcon
                icon={faChartBar}
                className="mr-2 text-blue-600"
              />
              KPI Analysis
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">ETA to Alta Difference:</span>
                <p
                  className={`font-medium ${
                    etaCompliance.compliant
                      ? "text-green-600"
                      : etaCompliance.daysDifference !== null
                      ? "text-red-600"
                      : "text-gray-600"
                  }`}
                >
                  {etaCompliance.daysDifference !== null
                    ? `${etaCompliance.daysDifference} days`
                    : "No data available"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">
                  Exit Time (ETA to Crossing):
                </span>
                <p className="font-medium">
                  {exitTime !== null ? `${exitTime} days` : "No data available"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">
                  Payment Time (ETA to Paid):
                </span>
                <p className="font-medium">
                  {paymentTime !== null
                    ? `${paymentTime} days`
                    : "No data available"}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Validation Process:</span>
                <p
                  className={`font-medium ${
                    outOfTimeValidation
                      ? "text-red-600"
                      : inValidationProcess
                      ? "text-yellow-600"
                      : "text-gray-600"
                  }`}
                >
                  {outOfTimeValidation
                    ? "Out of time"
                    : inValidationProcess
                    ? "In process"
                    : "Not in validation"}
                </p>
              </div>
            </div>
          </div>

          {/* Status Timeline */}
          {item.header.trafficStatusTimeline &&
            item.header.trafficStatusTimeline.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3 pb-2 border-b flex items-center">
                  <FontAwesomeIcon
                    icon={faHistory}
                    className="mr-2 text-blue-600"
                  />
                  Status Timeline
                </h4>
                <div className="space-y-4">
                  {item.header.trafficStatusTimeline.map(
                    (statusEntry, index) => (
                      <div
                        key={statusEntry._id || index}
                        className="flex items-start relative"
                      >
                        {/* Vertical timeline line */}
                        {index <
                          item.header.trafficStatusTimeline.length - 1 && (
                          <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-gray-200"></div>
                        )}

                        {/* Status indicator */}
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center z-10 ${
                            statusEntry.status
                              ? getStatusColorClass(statusEntry.status)
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          <span className="text-xs font-bold">{index + 1}</span>
                        </div>

                        <div className="ml-4 pb-4">
                          <p className="font-medium text-gray-800">
                            {statusEntry.status}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(statusEntry.fechaSatus)}
                          </p>
                          {statusEntry.actualizadoPor && (
                            <p className="text-xs text-gray-500 mt-1">
                              Updated by: {statusEntry.actualizadoPor}
                            </p>
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              Close
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium">
              <FontAwesomeIcon icon={faDownload} className="mr-1" />
              Export Details
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedReferencePanel;
